import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { EDMUNDO_FERIA_URL } from "@shared/../shared/constants";
import { ArrowRight, BookOpen, Lightbulb, Users, Shield, Star } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import Seo from "@/components/Seo";
import MentoringDialog from "@/components/MentoringDialog";
import { useLocale } from "@/contexts/LocaleContext";

const HOME_JSONLD: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Garantías Extraordinarias",
    url: "https://garantiasextraordinarias.com",
    inLanguage: "es",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Garantías Extraordinarias",
    url: "https://garantiasextraordinarias.com",
    logo: "https://garantiasextraordinarias.com/images/libro-portada.jpeg",
    founder: { "@type": "Person", name: "Edmundo Treviño" },
    sameAs: [
      "https://www.facebook.com/edmundotrevinomx",
      "https://www.youtube.com/@comprandoamerica",
      "https://www.linkedin.com/in/edmundotrevino/",
      "https://edmundotrevino.com",
    ],
  },
];

export default function Home() {
  const { data: blogData } = trpc.blog.listPublished.useQuery({ limit: 3, offset: 0 });
  const { data: ideasData } = trpc.ideas.listApproved.useQuery({ limit: 3, offset: 0 });
  const { t, locale } = useLocale();
  const aboutPath = locale === "en" ? "/about-the-book" : "/sobre-el-libro";
  const dateLocale = locale === "en" ? "en-US" : "es-MX";

  return (
    <div className="min-h-screen">
      <Seo
        title={
          locale === "en"
            ? "Extraordinary Warranties — 100 ideas to differentiate your business"
            : "Garantías Extraordinarias — 100 ideas para diferenciar tu empresa"
        }
        description={
          locale === "en"
            ? "Book, blog and community on extraordinary warranties: use disruptive promises to sell more, retain customers and stop competing on price. By Edmundo Treviño."
            : "Libro, blog y comunidad sobre garantías extraordinarias: cómo usar promesas disruptivas para vender más, fidelizar clientes y dejar de competir por precio. Por Edmundo Treviño."
        }
        path={locale === "en" ? "/en" : "/"}
        type="website"
        locale={locale}
        jsonLd={HOME_JSONLD.map(item => item["@type"] === "WebSite" ? { ...item, inLanguage: locale, url: `https://garantiasextraordinarias.com${locale === "en" ? "/en" : "/"}` } : item)}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark via-background to-navy-light opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,oklch(0.78_0.12_85/0.08),transparent_60%)]" />

        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-sm font-medium">
                <Star className="h-3.5 w-3.5" />
                {t("home.badge")}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight">
                {t("home.title1")}{" "}
                <span className="text-gold italic">{t("home.titleHighlight")}</span>.
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                <strong className="text-foreground">{t("home.descBold")}</strong> {t("home.desc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={`${aboutPath}#opciones`}>
                  <Button size="lg" className="bg-gold text-navy-dark hover:bg-gold-light font-semibold text-base px-8 w-full sm:w-auto">
                    {t("home.ctaBuy")}
                  </Button>
                </a>
                <Link href="/blog">
                  <Button size="lg" variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 font-semibold text-base px-8 w-full sm:w-auto">
                    {t("home.ctaExplore")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 to-transparent rounded-2xl blur-xl" />
                <img
                  src={EDMUNDO_FERIA_URL}
                  alt={locale === "en" ? "Edmundo Treviño presenting his book Extraordinary Warranties" : "Edmundo Treviño presentando su libro Garantías Extraordinarias"}
                  width={320}
                  height={420}
                  fetchPriority="high"
                  decoding="async"
                  className="relative rounded-xl shadow-2xl shadow-gold/10 max-w-[320px] w-full border border-gold/20 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-navy-light/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              {t("home.featuresTitle")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("home.featuresDesc")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-card border-border/50 hover:border-gold/30 transition-colors group">
              <CardContent className="p-6 text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <BookOpen className="h-6 w-6 text-gold" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-card-foreground">{t("home.feat1Title")}</h3>
                <p className="text-sm text-muted-foreground">{t("home.feat1Desc")}</p>
                <Link href="/blog">
                  <span className="inline-flex items-center text-sm text-gold font-medium hover:underline">
                    {t("home.feat1Link")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </span>
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/50 hover:border-gold/30 transition-colors group">
              <CardContent className="p-6 text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <Lightbulb className="h-6 w-6 text-gold" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-card-foreground">{t("home.feat2Title")}</h3>
                <p className="text-sm text-muted-foreground">{t("home.feat2Desc")}</p>
                <Link href="/ideas">
                  <span className="inline-flex items-center text-sm text-gold font-medium hover:underline">
                    {t("home.feat2Link")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </span>
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/50 hover:border-gold/30 transition-colors group">
              <CardContent className="p-6 text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <Users className="h-6 w-6 text-gold" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-card-foreground">{t("home.feat3Title")}</h3>
                <p className="text-sm text-muted-foreground">{t("home.feat3Desc")}</p>
                <MentoringDialog>
                  <button type="button" className="inline-flex items-center text-sm text-gold font-medium hover:underline">
                    {t("home.feat3Link")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </button>
                </MentoringDialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      {blogData && blogData.posts.length > 0 && (
        <section className="py-16">
          <div className="container">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-serif font-bold">{t("home.latestBlog")}</h2>
              <Link href="/blog">
                <span className="text-sm text-gold font-medium hover:underline inline-flex items-center">
                  {t("home.viewAll")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogData.posts.map((post) => (
                <Link key={post.id} href={`/blog/${post.slug}`}>
                  <Card className="bg-card border-border/50 hover:border-gold/30 transition-all hover:shadow-lg hover:shadow-gold/5 h-full">
                    <CardContent className="p-6 space-y-3">
                      {post.category && (
                        <span className="inline-block text-xs font-medium text-gold bg-gold/10 px-2.5 py-1 rounded-full">
                          {post.category}
                        </span>
                      )}
                      <h3 className="font-serif text-lg font-semibold text-card-foreground line-clamp-2">{post.title}</h3>
                      {post.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {new Date(post.createdAt).toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" })}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Reader Ideas */}
      {ideasData && ideasData.ideas.length > 0 && (
        <section className="py-16 bg-navy-light/30">
          <div className="container">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-serif font-bold">{t("home.communityIdeas")}</h2>
              <Link href="/ideas">
                <span className="text-sm text-gold font-medium hover:underline inline-flex items-center">
                  {t("home.viewAll")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ideasData.ideas.map((idea) => (
                <Card key={idea.id} className="bg-card border-border/50 hover:border-gold/30 transition-colors">
                  <CardContent className="p-6 space-y-3">
                    {idea.industry && (
                      <span className="inline-block text-xs font-medium text-gold bg-gold/10 px-2.5 py-1 rounded-full">
                        {idea.industry}
                      </span>
                    )}
                    <h3 className="font-serif text-lg font-semibold text-card-foreground">{idea.guaranteeTitle}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-3">{idea.guaranteeDescription}</p>
                    <p className="text-xs text-gold/70">— {idea.authorName}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-4">
        <div className="container">
          <NewsletterForm variant="hero" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="relative rounded-2xl overflow-hidden border border-gold/20">
            <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/5" />
            <div className="relative p-8 md:p-12 text-center space-y-6">
              <Shield className="h-12 w-12 text-gold mx-auto" />
              <h2 className="text-3xl md:text-4xl font-serif font-bold">
                {t("home.ctaTitle1")} <span className="text-gold italic">{t("home.ctaTitle2")}</span>?
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                {t("home.ctaDesc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={`${aboutPath}#opciones`}>
                  <Button size="lg" className="bg-gold text-navy-dark hover:bg-gold-light font-semibold px-8">
                    {t("home.ctaBuy")}
                  </Button>
                </a>
                <Link href="/ideas">
                  <Button size="lg" variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 font-semibold px-8">
                    {t("home.ctaSendIdea")} <Lightbulb className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
